import { IJob } from '@medely/types';
import { isNonEmptyArray } from '@mp/utils';
import ExpiringConditions from '@mp/components/taskCards/ExpiringConditions';
import React from 'react';
import { Platform, RenderCardMap } from './nextStepsHelpers';
import { NextStepsTemplate } from '.';
import {
  CampaignsNextStepCards,
  GetReady,
  ProPoolInvite,
  ProPoolOnboarding,
  ProPoolOrientation,
  SetYourselfUp,
} from '../taskCards';
import { ProfessionalTodos } from '@mp/hooks/useProfessionalTodos';
import { getNextStepsIcons } from './nextStepsIcons';
import { TrackNextStepCardClickPayload, useCurrentUser, useGate } from '@mp/hooks';
import { ReescreningAutomationCard } from '../taskCards/ReescreningAutomationCard';
import { gateNames } from '@mp/constants';

export type FullProfessionalTodosKeys =
  | 'campaigns_status'
  | 'w2_interest_card'
  | 'disputes_waiting_on_professional'
  | 'active_jobs_requesting_to_work_facility_ids'
  | 'enable_location_services'
  | 'unsigned_addendum_assignment_ids'
  | 'extension_offers'
  | 'assignment_offers'
  | 'pending_pro_pool_invite'
  | 'pending_pro_pool_onboarding'
  | 'pending_pro_pool_orientation'
  | 'days_until_debit_card_expires'
  | 'has_position_with_completed_onboarding'
  | 'assignments_missing_conditions'
  | 'expiring_conditions'
  | 'rescreening'
  | 'available_for_extensions'
  | 'finished_jobs_count'
  | 'set_yourself_up'
  | 'get_ready'
  | 'missing_w2_per_diem_onboarding'
  | 'missing_premier_credentials'
  | 'permanent_placement_offers';

interface BuildFullTodosOptions {
  todos: ProfessionalTodos;
  bookedJobs: IJob[];
  requestedJobs: IJob[];
  isGeolocationEnabled: boolean;
  expiringConditionsInfo?: { severity: string; title: string } | null;
  isW2OnboardingMissing?: boolean;
}
const FINISHED_JOBS_THRESHOLD = 5;
export function buildFullTodos({
  todos,
  bookedJobs,
  requestedJobs,
  isGeolocationEnabled,
  expiringConditionsInfo,
  isW2OnboardingMissing,
}: BuildFullTodosOptions) {
  const bookedJobsCount = todos?.booked_jobs_count ?? 0;
  const finishedJobsCount = todos?.finished_jobs_count ?? 0;
  const proHasOnboarded = todos?.has_position_with_completed_onboarding;
  const enable_location_services =
    isNonEmptyArray(bookedJobs) &&
    !isGeolocationEnabled &&
    bookedJobs.some((job) => job?.location?.enable_geo_fence);

  const active_jobs_requesting_to_work_facility_ids =
    (todos?.active_jobs_requesting_to_work_facility_ids as number[] | undefined)?.filter(
      (id: number) => requestedJobs.some((job) => job?.location?.id === id),
    ) ?? [];
  const shouldShowFinishedJobsCount = finishedJobsCount < FINISHED_JOBS_THRESHOLD;
  const shouldShowSetYourselfUp = !!(proHasOnboarded && bookedJobsCount === 0);
  const finalFinishedJobsCount =
    shouldShowFinishedJobsCount && !shouldShowSetYourselfUp ? finishedJobsCount : null;
  const finalSetYourselfUp = shouldShowSetYourselfUp;

  return {
    ...todos,
    enable_location_services,
    active_jobs_requesting_to_work_facility_ids,
    get_ready: !!(bookedJobsCount > 0 && finishedJobsCount < FINISHED_JOBS_THRESHOLD),
    finished_jobs_count: finalFinishedJobsCount,
    set_yourself_up: finalSetYourselfUp,
    expiring_conditions: expiringConditionsInfo ?? undefined,
    isW2OnboardingMissing: !!isW2OnboardingMissing,
    missing_w2_per_diem_onboarding: !!isW2OnboardingMissing,
  };
}

export function getRenderCardMap<T extends Record<string, any>>(
  platform: Platform,
  options: {
    onTrackClickPayload?: (payload: TrackNextStepCardClickPayload) => void;
    isProPoolEnabled?: boolean;
    isProPoolAutomationEnabled?: boolean;
    isAgencyPro?: boolean;
    recommendedConditions?: any[];
    openWebRedirect?: () => void;
    handleAllow?: () => void;
    proType?: 'marketplace' | 'agency' | 'irp';
  },
): RenderCardMap<T> {
  const reescreeningGate = useGate(gateNames.rescreening_automation);
  const { isAgency } = useCurrentUser();

  const icons = getNextStepsIcons();
  const shouldRenderFor = (
    allowedTypes: ('marketplace' | 'agency' | 'irp')[],
    currentType: 'marketplace' | 'agency' | 'irp' | undefined,
  ): boolean => {
    return allowedTypes.includes(currentType ?? 'marketplace');
  };

  const map: RenderCardMap<T> = {};

  map['expiring_conditions' as keyof T] = (data) =>
    shouldRenderFor(['marketplace', 'agency'], options.proType) && data ? (
      <ExpiringConditions title={data.title} severity={data.severity} />
    ) : null;

  map['rescreening' as keyof T] = (data) =>
    !!data && reescreeningGate ? <ReescreningAutomationCard key="rescreening" /> : null;

  map['assignment_offers' as keyof T] = (data) =>
    !isAgency && isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="assignment_offers"
        content="You have an assignment offer ready to review and accept."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={platform === 'web' ? '/my-assignments' : { pathname: 'Assignments' }}
        color="error"
      />
    ) : null;

  map['available_for_extensions' as keyof T] = (assignmentIds) =>
    Array.isArray(assignmentIds)
      ? assignmentIds.map((assignmentId: number) => (
          <NextStepsTemplate
            key={`available_for_extensions-${assignmentId}`}
            content="Let us know if you are interested in extending or finding new work"
            title="Your assignment is nearing completion"
            icon={icons.calendarDay}
            to={
              platform === 'web'
                ? {
                    pathname: `/assignments/${assignmentId}/details`,
                    state: { opensExtensionDialog: true },
                  }
                : {
                    pathname: 'AssignmentDetails',
                    state: {
                      id: assignmentId,
                      opensExtensionDialog: true,
                    },
                  }
            }
            color="default"
          />
        ))
      : null;

  map['extension_offers' as keyof T] = (data) =>
    !isAgency && isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="extension_offers"
        content="You have an assignment extension offer ready to review and accept."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={
          platform === 'web'
            ? `/available/assignments/${data[0]}`
            : { pathname: 'AvailableAssignmentDetails', state: { id: data[0] } }
        }
        color="error"
      />
    ) : null;

  map['unsigned_addendum_assignment_ids' as keyof T] = (data) =>
    isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="unsigned_addendum_assignment_ids"
        content="Please review and sign your latest assignment addendum."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={
          platform === 'web'
            ? `/assignments/${data[0]}/contracts`
            : { pathname: 'AssignmentDetails', state: { id: data[0] } }
        }
        color="error"
      />
    ) : null;

  map['has_position_with_completed_onboarding' as keyof T] = (data) =>
    !data ? (
      <NextStepsTemplate
        key="has_position_with_completed_onboarding"
        content={
          platform === 'web'
            ? 'Complete your onboarding to begin booking jobs.'
            : 'Complete your onboarding on our website to start booking jobs.'
        }
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={platform === 'web' ? '/onboarding' : { pathname: 'RedirectsToWebOnboarding' }}
        color="error"
      />
    ) : null;

  map['missing_premier_credentials' as keyof T] = (isMissingRecommended) =>
    !!isMissingRecommended && options.recommendedConditions?.length ? (
      <NextStepsTemplate
        key="missing_premier_credentials"
        content={
          platform === 'web'
            ? 'Complete your recommended credentials to unlock more opportunities, early access, and higher paying shifts.'
            : 'Complete your recommended credentials to unlock more shifts and higher rates.'
        }
        title="Unlock more shifts"
        icon={icons.award}
        to={
          platform === 'web'
            ? '/credentials/recommended'
            : { pathname: 'RootCredentials', state: { type: 'recommended' } }
        }
        color="default"
      />
    ) : null;

  map['days_until_debit_card_expires' as keyof T] = (daysUntilExpiration) =>
    shouldRenderFor(['marketplace'], options.proType) &&
    typeof daysUntilExpiration === 'number' &&
    daysUntilExpiration < 14 ? (
      <NextStepsTemplate
        key="days_until_debit_card_expires"
        content="Update your expiring debit card."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={
          platform === 'web'
            ? { pathname: '/settings/financial/payout-method', state: { selected: 3 } }
            : { pathname: 'PayoutMethod' }
        }
        color="error"
      />
    ) : null;

  map['campaigns_status' as keyof T] = (data) =>
    isNonEmptyArray(data) ? <CampaignsNextStepCards key="campaigns_status" data={data} /> : null;

  map['pending_pro_pool_invite' as keyof T] = (data) =>
    data && options.isProPoolEnabled ? (
      <ProPoolInvite
        key="pending_pro_pool_invite"
        locationId={data.location_id}
        facilityName={data.location_name}
        professionalQualificationId={data.professional_qualification_id}
        plan={data.plan}
      />
    ) : null;

  map['pending_pro_pool_onboarding' as keyof T] = (data) =>
    data && options.isProPoolEnabled ? (
      <ProPoolOnboarding
        key="pending_pro_pool_onboarding"
        locationId={data.location_id}
        facilityName={data.location_name}
      />
    ) : null;

  map['pending_pro_pool_orientation' as keyof T] = (data) =>
    data && options.isProPoolAutomationEnabled ? (
      <ProPoolOrientation
        key="pending_pro_pool_orientation"
        locationName={data.location_name}
        poolId={data.pool_id}
        positionNames={data.position_names}
        shiftType={data.shift_type}
      />
    ) : null;

  map['finished_jobs_count' as keyof T] = (data) =>
    shouldRenderFor(['marketplace'], options.proType) &&
    typeof data === 'number' &&
    data < FINISHED_JOBS_THRESHOLD ? (
      <SetYourselfUp
        key="finished_jobs_count"
        title="Set yourself up for success"
        body="Learn how to find and book shifts with Medely."
        iconName="light-info-circle"
      />
    ) : null;

  map['get_ready' as keyof T] = (data) => (data ? <GetReady key="get_ready" /> : null);

  map['set_yourself_up' as keyof T] = (data) =>
    data && shouldRenderFor(['marketplace', 'irp'], options.proType) ? (
      <SetYourselfUp key="set_yourself_up" />
    ) : null;

  map['missing_w2_per_diem_onboarding' as keyof T] = (data) =>
    shouldRenderFor(['marketplace'], options.proType) && data ? (
      <NextStepsTemplate
        key="missing_w2_per_diem_onboarding"
        icon={icons.fileInvoiceDollar}
        title="W-2 Shifts"
        content={
          platform === 'web'
            ? 'Complete your W-2 paperwork for eligibility to work W-2 shifts offered by Frontline Health.'
            : 'Complete your W-2 paperwork on our website for eligibility to work W-2 shifts offered by Frontline Health.'
        }
        color="info"
        to={platform === 'web' ? '/onboarding/w2' : { pathname: 'GoToW2Onboarding' }}
      />
    ) : null;

  map['assignments_missing_conditions' as keyof T] = (data) =>
    isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="assignments_missing_conditions"
        icon={icons.triangleExclamation}
        badge="Needs Attention"
        content={
          platform === 'web'
            ? 'Finish onboarding for your upcoming assignment.'
            : 'Finish onboarding on our website for your upcoming assignment.'
        }
        color="error"
        to={
          platform === 'web'
            ? `/assignments/${data[0]}/onboarding`
            : { pathname: 'RedirectsToWebOnboarding' }
        }
        external={platform === 'mobile' ? true : false}
      />
    ) : null;

  map['enable_location_services' as keyof T] = (data) =>
    shouldRenderFor(['marketplace', 'agency'], options.proType) && data ? (
      <NextStepsTemplate
        key="enable_location_services"
        icon={platform === 'web' ? icons.locationCrosshairs : icons.location}
        title="Enable location services"
        content="Location sharing is required to clock in for your next shift. Enable location services now."
        color="default"
        onPress={options.handleAllow}
      />
    ) : null;

  //TODO: Move to MP once DisputeCardWrapper is migrated
  // map['disputes_waiting_on_professional' as keyof T] = (data) =>
  //   isNonEmptyArray(data) ? (
  //     <ExpiringConditions
  //       title="Disputes Waiting on You"
  //       severity="info"
  //       onPress={() => options.onTrackClick?.('disputes_waiting_on_professional' as keyof T, 1)}
  //     />
  //   ) : null;

  //TODO: Move to MP once MultipleRequestsFromFacility is migrated
  // map['active_jobs_requesting_to_work_facility_ids' as keyof T] = (data) =>
  //   isNonEmptyArray(data) ? (
  //     <ExpiringConditions
  //       title="Active Job Requests"
  //       severity="info"
  //       onPress={() =>
  //         options.onTrackClick?.('active_jobs_requesting_to_work_facility_ids' as keyof T, 1)
  //       }
  //     />
  //   ) : null;

  return map;
}
